<template>
  <div class="page">
    <header class="page-header">
      <div>
        <p class="eyebrow">{{ t('questions') }}</p>
        <h1>{{ questionsData?.itemTitle || t('itemLoading') }}</h1>
      </div>
      <router-link class="secondary" :to="eventLink">{{ t('backToEvent') }}</router-link>
    </header>

    <section v-if="isLoading" class="card">{{ t('loadingQuestions') }}</section>
    <section v-else-if="errorMessage" class="card error">{{ errorMessage }}</section>

    <section v-else-if="questionsData" class="card">
      <div v-if="itemData" class="item-summary">
        <div v-if="itemData.coverImageUrl" class="item-image">
          <img :src="itemData.coverImageUrl" :alt="itemData.title" />
        </div>
        <div>
          <h2>{{ itemData.title }}</h2>
          <p v-if="itemData.description" class="item-description">{{ itemData.description }}</p>
          <div v-if="itemData.socialLinks?.length" class="item-socials">
            <a
              v-for="link in itemData.socialLinks"
              :key="link"
              class="social-link"
              :href="link"
              target="_blank"
              rel="noopener"
            >
              <span class="social-icon" :class="getSocialType(link)">
                <span v-if="getSocialType(link) === 'instagram'">
                  <img src="/icons/instagram.png" alt="Instagram" width="14" height="14" />
                </span>
                <span v-else-if="getSocialType(link) === 'tiktok'">
                  <img src="/icons/tiktok.png" alt="TikTok" width="14" height="14" />
                </span>
                <span v-else-if="getSocialType(link) === 'twitter'">
                  <img src="/icons/twitter.png" alt="Twitter" width="14" height="14" />
                </span>
                <span v-else-if="getSocialType(link) === 'pinterest'">
                  <img src="/icons/pinterest.png" alt="Pinterest" width="14" height="14" />
                </span>
                <span v-else>
                  <span class="link-prefix">www</span>
                </span>
              </span>
            </a>
          </div>
        </div>
      </div>
      <form class="question-form" @submit.prevent="submitForm">
        <article v-for="question in questionsData.questions" :key="question.id" class="question">
          <div class="question-header">
            <h3>{{ question.text }}</h3>
            <span v-if="question.isRequired" class="required">{{ t('required') }}</span>
          </div>

          <div v-if="isStar(question)" class="field">
            <label>{{ t('rating') }}</label>
            <div class="stars" role="radiogroup" :aria-label="question.text">
              <template v-for="value in STAR_VALUES" :key="value">
                <input
                  :id="`star-${question.id}-${value}`"
                  type="radio"
                  :name="`star-${question.id}`"
                  :value="value"
                  v-model.number="answersById[question.id].starValue"
                  :required="question.isRequired"
                />
                <label class="star" :for="`star-${question.id}-${value}`">
                  <span aria-hidden="true">★</span>
                  <span class="sr-only">{{ value }} yıldız</span>
                </label>
              </template>
            </div>
          </div>

          <div v-else-if="isOpenText(question)" class="field">
            <label>{{ t('answer') }}</label>
            <textarea
              v-model.trim="answersById[question.id].openText"
              rows="4"
              :required="question.isRequired"
            ></textarea>
          </div>

          <div v-else-if="isMultipleChoice(question)" class="field">
            <label>{{ t('selectOption') }}</label>
            <div class="options">
              <label v-for="option in question.options" :key="option.key" class="option">
                <input
                  type="radio"
                  :name="`option-${question.id}`"
                  :value="option.key"
                  v-model="answersById[question.id].selectedOptionKey"
                  :required="question.isRequired"
                />
                <span>{{ option.text }}</span>
              </label>
            </div>
          </div>
        </article>

        <div class="actions">
          <button class="primary" type="submit" :disabled="isSubmitting">{{ t('submitAnswers') }}</button>
          <p v-if="submitError" class="error-message">{{ submitError }}</p>
        </div>
      </form>
    </section>
    <section v-else class="card">{{ t('loadingQuestions') }}</section>
  </div>
</template>

<script>
import { onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getAnswerStatus, getItemByShortCode, getItemQuestions, submitAnswers } from '../services/publicApi'
import { useI18n } from '../i18n'

const TYPE_MULTIPLE_CHOICE = 0
const TYPE_STAR = 1
const TYPE_OPEN_TEXT = 2
const STAR_VALUES = [5, 4, 3, 2, 1]

export default {
  name: 'EventItemQuestionsPage',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const questionsData = ref(null)
    const answersById = reactive({})
    const isLoading = ref(true)
    const errorMessage = ref('')
    const submitError = ref('')
    const isSubmitting = ref(false)
    const itemData = ref(null)
    const { t } = useI18n()

    const eventLink = {
      name: 'event',
      params: { eventSlug: route.params.eventSlug }
    }

    const loadQuestions = async () => {
      isLoading.value = true
      errorMessage.value = ''
      try {
        const status = await getAnswerStatus(route.params.eventSlug, route.params.itemShortCode)
        if (status?.isFinished) {
          router.replace({
            name: 'event-winner',
            params: {
              eventSlug: route.params.eventSlug
            }
          })
          return
        }
        if (status?.hasAnswered) {
          router.replace({
            name: 'event-item-submitted',
            params: {
              eventSlug: route.params.eventSlug,
              itemShortCode: route.params.itemShortCode
            }
          })
          return
        }

        const [questionsResponse, itemResponse] = await Promise.all([
          getItemQuestions(route.params.eventSlug, route.params.itemShortCode),
          getItemByShortCode(route.params.eventSlug, route.params.itemShortCode).catch(() => null)
        ])
        questionsData.value = questionsResponse
        itemData.value = itemResponse
        questionsData.value.questions.forEach((question) => {
          answersById[question.id] = {
            questionId: question.id,
            type: question.type,
            starValue: '',
            openText: '',
            selectedOptionKey: ''
          }
        })
      } catch (error) {
        errorMessage.value = error.status === 404
          ? t('itemOrEventNotFound')
          : t('questionsLoadFailed')
      } finally {
        isLoading.value = false
      }
    }

    const isStar = (question) => question.type === TYPE_STAR
    const isOpenText = (question) => question.type === TYPE_OPEN_TEXT
    const isMultipleChoice = (question) => question.type === TYPE_MULTIPLE_CHOICE

    const getSocialType = (link) => {
      const lower = link.toLowerCase()
      if (lower.includes('instagram.com')) return 'instagram'
      if (lower.includes('tiktok.com')) return 'tiktok'
      if (lower.includes('twitter.com') || lower.includes('x.com')) return 'twitter'
      if (lower.includes('pinterest.com')) return 'pinterest'
      return 'www'
    }

    const buildPayload = () => ({
      eventId: questionsData.value.eventId,
      itemId: questionsData.value.itemId,
      answers: questionsData.value.questions
        .map((question) => {
          const base = answersById[question.id]
          if (question.type === TYPE_STAR) {
            return base.starValue
              ? {
                  questionId: question.id,
                  type: question.type,
                  starValue: Number(base.starValue)
                }
              : null
          }
          if (question.type === TYPE_OPEN_TEXT) {
            const text = (base.openText || '').trim()
            return text
              ? {
                  questionId: question.id,
                  type: question.type,
                  openText: text
                }
              : null
          }
          if (question.type === TYPE_MULTIPLE_CHOICE) {
            return base.selectedOptionKey
              ? {
                  questionId: question.id,
                  type: question.type,
                  selectedOptionKey: base.selectedOptionKey
                }
              : null
          }
          return null
        })
        .filter(Boolean)
    })

    const submitForm = async () => {
      if (!questionsData.value) return
      submitError.value = ''
      isSubmitting.value = true
      try {
        await submitAnswers(
          route.params.eventSlug,
          route.params.itemShortCode,
          buildPayload()
        )
        router.replace({
          name: 'event-item-submitted',
          params: {
            eventSlug: route.params.eventSlug,
            itemShortCode: route.params.itemShortCode
          }
        })
      } catch (error) {
        if (error.status === 405 || error.status === 409) {
          submitError.value = t('alreadySubmitted')
        } else {
          submitError.value = error.status === 400
            ? t('reviewAnswers')
            : t('submitFailed')
        }
      } finally {
        isSubmitting.value = false
      }
    }

    onMounted(loadQuestions)

    return {
      questionsData,
      itemData,
      answersById,
      isLoading,
      errorMessage,
      submitError,
      isSubmitting,
      eventLink,
      t,
      STAR_VALUES,
      isStar,
      isOpenText,
      isMultipleChoice,
      getSocialType,
      submitForm
    }
  }
}
</script>

<style scoped>
.page {
  display: grid;
  gap: 20px;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.eyebrow {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 8px;
}

.subtext {
  color: #64748b;
  margin: 4px 0 0;
}

.card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.error {
  color: #b91c1c;
  border: 1px solid #fecaca;
  background: #fff1f2;
}



.question-form {
  display: grid;
  gap: 20px;
}

.item-summary {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  align-items: center;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  margin-bottom: 20px;
}

.item-image {
  width: 88px;
  height: 88px;
  border-radius: 18px;
  overflow: hidden;
  background: #e2e8f0;
  flex-shrink: 0;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-description {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 14px;
}

.item-socials {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.social-link {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.social-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #1e293b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #ffffff;
}

.social-icon.instagram,
.social-icon.tiktok,
.social-icon.twitter,
.social-icon.pinterest {
  background: #ffffff;
}

.question {
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  display: grid;
  gap: 12px;
}

.question-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.required {
  background: #fee2e2;
  color: #991b1b;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.field {
  display: grid;
  gap: 8px;
}

textarea {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
}

.stars {
  display: flex;
  flex-direction: row-reverse;
  gap: 8px;
  position: relative;
}

.stars input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 1px;
  height: 1px;
}

.star {
  cursor: pointer;
  font-size: 28px;
  line-height: 1;
  color: #cbd5f5;
  display: inline-flex;
  align-items: center;
}

.star span {
  transition: color 0.2s ease;
}

.stars input:checked ~ label span,
.stars input:focus-visible ~ label span,
.star:hover span,
.star:hover ~ .star span {
  color: #f59e0b;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.options {
  display: grid;
  gap: 8px;
}

.option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.hint {
  color: #94a3b8;
  font-size: 12px;
}

.actions {
  display: grid;
  gap: 10px;
}

.primary {
  background: #2563eb;
  border: none;
  color: #ffffff;
  padding: 12px 18px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
}

.primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error-message {
  color: #b91c1c;
}

.secondary {
  background: #e2e8f0;
  color: #0f172a;
  padding: 10px 16px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
}
</style>
